import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from './client'
import type {
  Dashboard, Panel,
  CreateDashboardRequest, CreatePanelRequest, UpdatePanelRequest,
  PromQueryResponse, LogEntry,
} from '@/types'

// ── Dashboard CRUD ─────────────────────────────────────────────────────────

export function useDashboards(tenantId: string | null) {
  const api = useApiClient()
  return useQuery<Dashboard[]>({
    queryKey: ['dashboards', tenantId],
    queryFn: () => api.get<Dashboard[]>(`/api/v1/tenants/${tenantId}/dashboards`),
    enabled: !!tenantId,
    staleTime: 30_000,
  })
}

export function useDashboard(tenantId: string | null, dashboardId: string | null) {
  const api = useApiClient()
  return useQuery<Dashboard>({
    queryKey: ['dashboard', tenantId, dashboardId],
    queryFn: () => api.get<Dashboard>(`/api/v1/tenants/${tenantId}/dashboards/${dashboardId}`),
    enabled: !!tenantId && !!dashboardId,
    staleTime: 30_000,
  })
}

export function useCreateDashboard(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateDashboardRequest) =>
      api.post<Dashboard>(`/api/v1/tenants/${tenantId}/dashboards`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboards', tenantId] }),
  })
}

export function useDeleteDashboard(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dashboardId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/dashboards/${dashboardId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboards', tenantId] }),
  })
}

// ── Panel CRUD ─────────────────────────────────────────────────────────────

export function useCreatePanel(tenantId: string, dashboardId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreatePanelRequest) =>
      api.post<Panel>(`/api/v1/tenants/${tenantId}/dashboards/${dashboardId}/panels`, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', tenantId, dashboardId] })
      qc.invalidateQueries({ queryKey: ['dashboards', tenantId] })
    },
  })
}

export function useUpdatePanel(tenantId: string, dashboardId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ panelId, req }: { panelId: string; req: UpdatePanelRequest }) =>
      api.put<Panel>(`/api/v1/tenants/${tenantId}/dashboards/${dashboardId}/panels/${panelId}`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', tenantId, dashboardId] }),
  })
}

export function useDeletePanel(tenantId: string, dashboardId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (panelId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/dashboards/${dashboardId}/panels/${panelId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', tenantId, dashboardId] }),
  })
}

export function useReorderPanels(tenantId: string, dashboardId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (panelIds: string[]) =>
      api.put(`/api/v1/tenants/${tenantId}/dashboards/${dashboardId}/panels/reorder`, { panel_ids: panelIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', tenantId, dashboardId] }),
  })
}

// ── Query proxy ────────────────────────────────────────────────────────────

export interface QueryRangeOpts {
  query: string
  from?: string
  to?: string
  step?: string
}

export function useQueryRange(
  tenantId: string | null,
  opts: QueryRangeOpts,
  enabled = true,
) {
  const api = useApiClient()
  return useQuery<PromQueryResponse>({
    queryKey: ['query_range', tenantId, opts],
    queryFn: () => {
      const params = new URLSearchParams({ query: opts.query })
      if (opts.from) params.set('from', opts.from)
      if (opts.to)   params.set('to', opts.to)
      if (opts.step) params.set('step', opts.step)
      return api.get<PromQueryResponse>(
        `/api/v1/tenants/${tenantId}/telemetry/query_range?${params}`,
      )
    },
    enabled: !!tenantId && enabled,
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export interface LogQueryOpts {
  query: string
  limit?: number
  from?: string
  to?: string
}

export function useLogQuery(
  tenantId: string | null,
  opts: LogQueryOpts,
  enabled = true,
) {
  const api = useApiClient()
  return useQuery<LogEntry[]>({
    queryKey: ['log_query', tenantId, opts],
    queryFn: () => {
      const params = new URLSearchParams({ query: opts.query })
      if (opts.limit) params.set('limit', String(opts.limit))
      if (opts.from)  params.set('from', opts.from)
      if (opts.to)    params.set('to', opts.to)
      return api.get<LogEntry[]>(
        `/api/v1/tenants/${tenantId}/telemetry/log_query?${params}`,
      )
    },
    enabled: !!tenantId && enabled,
    staleTime: 15_000,
    refetchInterval: 15_000,
  })
}

